import React, { useCallback, useMemo } from "react"
import { Box, useTheme } from "@mui/material"
import { useNavigate } from "react-router-dom"

import { getIcon } from "utils/icon"

import { useEvent } from "extensions/iconsiam/hooks/event/useEvent"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"

import { PageCard, PageCardContent } from "components/card"
import { PercentIcon } from "components/icons"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { Carousel, CarouselItem } from "components/Carousel"
import { EmptyData } from "components/EmptyData"
import { SidebarMenu } from "extensions/iconsiam/components/navigation/kiosk-h/SidebarMenu"
import { ContentWithAside } from "layouts/kiosk-h"
import { VenueSelector } from "components/VenueSelector"
import { VerticalEventCard } from "extensions/iconsiam/components/domains/events"

export const EventsKioskHPage = ({
  Layout = ContentWithAside,
  AppNavigation = DefaultAppNavigation,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { changeVenueFilter, locationVenueId, venueFilter, events } = useEvent()
  const { venues, isMultiVenueProject } = useVenue()
  const theme = useTheme()
  const slidePerPage = 3
  const carouselType = events && events.length < slidePerPage ? "slide" : "loop"
  const isAutoplay = events && events.length > slidePerPage

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.events) || <PercentIcon />,
    [theme]
  )
  const { styleOverrides } = theme.components?.[`EventPage`] || {}
  const { eventCarouselOption } =
    theme.components[`EventPage`]?.defaultProps || {}
  const {
    pageCard,
    pageCardContent,
    carouselContainer,
    carousel,
    emptyDataContainer,
    venueSelector,
  } = styleOverrides

  const handleClickSlide = useCallback(
    (_splide, { slide }, _event) => {
      navigate(slide.getAttribute("link"))
    },
    [navigate]
  )

  usePageSideBarLayout({
    sidebar: {
      open: false,
    },
  })

  const direction = theme.direction

  return (
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent sx={pageCardContent}>
          {!events || events.length <= 0 ? (
            <Box sx={emptyDataContainer}>
              <EmptyData
                icon={emptyIcon}
                primary={t("No Current Events")}
                secondary={t("Check Back Soon")}
              />
            </Box>
          ) : (
            <Box sx={carouselContainer}>
              <Carousel
                onClick={handleClickSlide}
                options={{
                  type: carouselType,
                  perPage: slidePerPage,
                  pagination: true,
                  trimSpace: false,
                  autoplay: isAutoplay,
                  gap: "24px",
                  focus: "center",
                  direction: direction,
                  ...eventCarouselOption,
                }}
                sx={carousel}
              >
                {events.map((event) => {
                  const sponsoredId = event?.properties?.sponsoredId || ""
                  return (
                    <CarouselItem
                      link={`/events/${event.id}`}
                      key={`${sponsoredId}${event.id}`}
                    >
                      <VerticalEventCard event={event} />
                    </CarouselItem>
                  )
                })}
              </Carousel>
            </Box>
          )}
          {isMultiVenueProject && (
            <Box sx={venueSelector}>
              <VenueSelector
                selectedVenue={venueFilter}
                venues={venues}
                onClickVenue={changeVenueFilter}
                currentVenue={locationVenueId}
              />
            </Box>
          )}
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
